import { Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import styled from 'styled-components';

import ClickSvg from '../../assets/icons/ClickSvg';
import useAccessBadge, { LicenseCoarseLevels } from '../../hooks/useAccessBadge';
import { useAttachmentContent } from '../../hooks/useAttachmentContent';
import { DesignerActions, useDesignerContext } from '../../hooks/useDesignerState';
import { useMetrics } from '../../hooks/useMetrics';
import usePaywallPrompt from '../../hooks/usePaywallPrompt';
import { useTextToSpeech } from '../../hooks/useTextToSpeech';
import { useRegisterLivePassage } from '../../providers/LivePassageProvider';
import { LessonAttachment } from '../../services/backendService/backendService';
import { IconWrapper } from '../_atoms/IconWrapper';
import ReadAloudHighlightedTextViewer from '../ReadAloud/ReadAloudHighlightedTextViewer';
import VoiceModeControls, { VOICE_PRESETS } from '../ReadAloud/VoiceModeControls';
import { ReadAloudModal } from '../ReadAloudModal';
import ReadAloudTextViewer from './ReadAloudTextViewer';
import { parseSlateStringToText } from './slateUtil';
import { SlideElementAttachmentPassageType } from './slide.types';

const readingStateDefaults = { size: 24 };

const TTS_ENABLED_COUNTRIES = ['US', 'GB', 'AU', 'CA', 'IE', 'NZ'];

export const ReadAloud = ({
  lessonAttachment,
  isFromDiscover,
}: {
  lessonAttachment: LessonAttachment;
  isFromDiscover: boolean;
}) => {
  const { content } = useAttachmentContent(lessonAttachment, isFromDiscover);

  const [readingState, setReadingState] = useState<{ size: number }>(
    readingStateDefaults,
  );

  const handleIncreaseFontSize = () => {
    setReadingState(prev => ({ ...prev, size: prev.size + 2 }));
  };
  const handleReduceFontSize = () => {
    setReadingState(prev => ({ ...prev, size: prev.size - 2 }));
  };

  const contentString = useMemo(() => {
    if (!content) return '';
    return JSON.stringify(content);
  }, [content]);

  const { accessBadge } = useAccessBadge();
  const country = accessBadge?.organization?.country;
  const isTTSEnabled = !!country && TTS_ENABLED_COUNTRIES.includes(country);
  const tts = useTextToSpeech(isTTSEnabled ? contentString : null);

  const { promptPaywall, paywallComponent, userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const coarseLevel = userAccessBadge?.license.coarseLevel;
  // US tenants need School plan (Enterprise); other supported countries (GB/AU/CA/IE/NZ) need Premium or higher.
  const isAllowed =
    country === 'US'
      ? coarseLevel === LicenseCoarseLevels.enterprise
      : coarseLevel === LicenseCoarseLevels.premium ||
        coarseLevel === LicenseCoarseLevels.enterprise;
  const metrics = useMetrics();

  const handleGenerate = (voiceId: string) => {
    if (!isAllowed) {
      metrics.logEvent('TTS.PaywallTriggered', {
        attachmentId: lessonAttachment.attachmentId,
        voiceId,
        country,
        coarseLevel,
      });
      promptPaywall({ label: 'Read Aloud TTS', description: 'Text to speech' });
      return;
    }
    metrics.logEvent('TTS.Generate', {
      attachmentId: lessonAttachment.attachmentId,
      voiceId,
      voiceLabel: VOICE_PRESETS.find(v => v.voiceId === voiceId)?.label,
    });
    tts.generate(voiceId);
  };

  // Stop TTS on unmount (e.g. modal close)
  useEffect(() => {
    return () => {
      tts.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isVoiceActive =
    tts.state === 'playing' || tts.state === 'paused' || tts.state === 'loading';

  return (
    <ReadAloudContainer>
      <ControlPanelContainer>
        <ControlPanel>
          <Tooltip title="Increase text size" placement="left">
            <ControlPanelButtonEnlarge onClick={handleIncreaseFontSize}>
              <span>A</span>
            </ControlPanelButtonEnlarge>
          </Tooltip>
          <Tooltip title="Decrease text size" placement="left">
            <ControlPanelButtonMinimize onClick={handleReduceFontSize}>
              <span>A</span>
            </ControlPanelButtonMinimize>
          </Tooltip>
          {isTTSEnabled && (
            <>
              <Divider />
              <VoiceModeControls
                state={tts.state}
                error={tts.error}
                analyserNode={tts.analyserNode}
                volume={tts.volume}
                playbackRate={tts.playbackRate}
                attachmentId={lessonAttachment.attachmentId}
                onGenerate={handleGenerate}
                onPlay={tts.play}
                onPause={tts.pause}
                onStop={tts.stop}
                onSeek={tts.seek}
                onVolumeChange={tts.setVolume}
                onPlaybackRateChange={tts.setPlaybackRate}
              />
            </>
          )}
        </ControlPanel>
      </ControlPanelContainer>
      {isTTSEnabled && isVoiceActive && tts.wordTimings.length > 0 ? (
        <ReadAloudHighlightedTextViewer
          content={contentString}
          id={lessonAttachment.attachmentId}
          forceState={{
            size: readingState.size,
            align: 'left',
            color: 'black',
          }}
          wordTimings={tts.wordTimings}
          currentWordIndex={tts.currentWordIndex}
        />
      ) : (
        <ReadAloudTextViewer
          content={contentString}
          id={lessonAttachment.attachmentId}
          forceState={{
            size: readingState.size,
            align: 'left',
            color: 'black',
          }}
        />
      )}
      {paywallComponent}
    </ReadAloudContainer>
  );
};

/** Same as ReadAloud but takes content directly (Slate JSON string) instead of loading via useAttachmentContent. */
export const ReadAloudWithContent = ({
  content,
  id = 'read-aloud-with-content',
}: {
  content: string;
  id?: string;
}) => {
  const [readingState, setReadingState] = useState<{ size: number }>(
    readingStateDefaults,
  );

  const handleIncreaseFontSize = () => {
    setReadingState(prev => ({ ...prev, size: prev.size + 2 }));
  };
  const handleReduceFontSize = () => {
    setReadingState(prev => ({ ...prev, size: prev.size - 2 }));
  };

  const { accessBadge } = useAccessBadge();
  const country = accessBadge?.organization?.country;
  const isTTSEnabled = !!country && TTS_ENABLED_COUNTRIES.includes(country);
  const tts = useTextToSpeech(isTTSEnabled ? content : null);

  const { promptPaywall, paywallComponent, userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const coarseLevel = userAccessBadge?.license.coarseLevel;
  const isAllowed =
    country === 'US'
      ? coarseLevel === LicenseCoarseLevels.enterprise
      : coarseLevel === LicenseCoarseLevels.premium ||
        coarseLevel === LicenseCoarseLevels.enterprise;
  const metrics = useMetrics();

  const handleGenerate = (voiceId: string) => {
    if (!isAllowed) {
      metrics.logEvent('TTS.PaywallTriggered', {
        attachmentId: id,
        voiceId,
        country,
        coarseLevel,
      });
      promptPaywall({ label: 'Read Aloud TTS', description: 'Text to speech' });
      return;
    }
    metrics.logEvent('TTS.Generate', {
      attachmentId: id,
      voiceId,
      voiceLabel: VOICE_PRESETS.find(v => v.voiceId === voiceId)?.label,
    });
    tts.generate(voiceId);
  };

  useEffect(() => {
    return () => {
      tts.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isVoiceActive =
    tts.state === 'playing' || tts.state === 'paused' || tts.state === 'loading';

  return (
    <ReadAloudContainer>
      <ControlPanelContainer>
        <ControlPanel>
          <Tooltip title="Increase font size" placement="left">
            <ControlPanelButtonEnlarge onClick={handleIncreaseFontSize}>
              <span>A</span>
            </ControlPanelButtonEnlarge>
          </Tooltip>
          <Tooltip title="Decrease font size" placement="left">
            <ControlPanelButtonMinimize onClick={handleReduceFontSize}>
              <span>A</span>
            </ControlPanelButtonMinimize>
          </Tooltip>
          {isTTSEnabled && (
            <>
              <Divider />
              <VoiceModeControls
                state={tts.state}
                error={tts.error}
                analyserNode={tts.analyserNode}
                volume={tts.volume}
                playbackRate={tts.playbackRate}
                attachmentId={id}
                onGenerate={handleGenerate}
                onPlay={tts.play}
                onPause={tts.pause}
                onStop={tts.stop}
                onSeek={tts.seek}
                onVolumeChange={tts.setVolume}
                onPlaybackRateChange={tts.setPlaybackRate}
              />
            </>
          )}
        </ControlPanel>
      </ControlPanelContainer>
      {isTTSEnabled && isVoiceActive && tts.wordTimings.length > 0 ? (
        <ReadAloudHighlightedTextViewer
          content={content}
          id={id}
          forceState={{
            size: readingState.size,
            align: 'left',
            color: 'black',
          }}
          wordTimings={tts.wordTimings}
          currentWordIndex={tts.currentWordIndex}
        />
      ) : (
        <ReadAloudTextViewer
          content={content}
          id={id}
          forceState={{
            size: readingState.size,
            align: 'left',
            color: 'black',
          }}
        />
      )}
      {paywallComponent}
    </ReadAloudContainer>
  );
};

const ControlPanelButton = styled.div`
  color: white;
  width: 100%;
  text-align: center;
  height: 50px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: black;
  }
  border-radius: 5px;
  padding: 10px;
  span {
    user-select: none;
  }
`;

const ControlPanelButtonMinimize = styled(ControlPanelButton)`
  font-size: 24px;
  line-height: 20px;
`;
const ControlPanelButtonEnlarge = styled(ControlPanelButton)`
  font-size: 38px;
  line-height: 32px;
`;

const ControlPanelContainer = styled.div`
  position: absolute;
  top: 50%;
  left: -85px;
  transform: translateY(-50%);
  border-radius: 10px;
  padding: 5px;
  background: rgba(17, 23, 29, 0.6);
  color: white;
  box-shadow: 0px 0px 0px 1px rgba(64, 87, 109, 0.04),
    0px 6px 20px -4px rgba(64, 87, 109, 0.3);

  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
`;

const ControlPanel = styled.div`
  position: relative;
`;

const Divider = styled.div`
  height: 1px;
  width: 80%;
  margin: 4px auto;
  background: rgba(255, 255, 255, 0.2);
`;

const ReadAloudContainer = styled.div`
  background: white;
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const SlideElementAttachmentPassage = ({
  element,
  lessonAttachments,
  slideMode,
  isFromDiscover,
}: {
  element: SlideElementAttachmentPassageType;
  style?: React.CSSProperties;
  lessonAttachments?: LessonAttachment[];
  slideMode: 'view' | 'edit';
  isFromDiscover: boolean;
}) => {
  const lessonAttachment = useMemo(
    () => lessonAttachments?.find(l => l.attachmentId === element.attachmentId),
    [lessonAttachments, element.attachmentId],
  );

  const title = useMemo(() => {
    if (!lessonAttachment) return 'No title';
    return parseSlateStringToText(lessonAttachment.title);
  }, [lessonAttachment]);

  // In view mode the open/closed state is sourced from the
  // LivePassageProvider when it's mounted (LiveGame), so the clicker
  // can drive open-next / close-current across every attachment on the
  // slide. Outside a LivePassageProvider (discover previews, student
  // views, designer thumbnails) we fall back to local state so a tap
  // still opens the modal. In edit mode the designer's own preview
  // state drives it (unchanged).
  const [localShowReadAloud, setLocalShowReadAloud] = useState(false);
  const livePassage = useRegisterLivePassage(element.id, slideMode === 'view');
  const showReadAloudInView = livePassage ? livePassage.isOpen : localShowReadAloud;
  const setShowReadAloudInView = (open: boolean) => {
    if (livePassage) livePassage.setOpen(open);
    else setLocalShowReadAloud(open);
  };

  const dispatch = useDesignerContext(c => c.dispatch);
  const previewElementId = useDesignerContext(
    c => c.state.ui.previewAttachmentPassageElementId,
  );
  const isPreviewOpenInEdit = slideMode === 'edit' && previewElementId === element.id;

  const showReadAloud = slideMode === 'view' ? showReadAloudInView : isPreviewOpenInEdit;

  const closeReadAloud = () => {
    if (slideMode === 'view') {
      setShowReadAloudInView(false);
    } else {
      dispatch({ type: DesignerActions.closeAttachmentPassagePreview });
    }
  };

  return (
    <div
      style={{
        background: '#3e7e69',
        height: '100%',
        width: '100%',
        borderRadius: 10,
        cursor: slideMode === 'edit' ? 'default' : 'pointer',
        boxShadow: '4px 8px 1px #61b195',
        position: 'relative',
        overflow: 'hidden',
        padding: '20px',
      }}
      onClick={() => {
        if (slideMode === 'view') {
          setShowReadAloudInView(true);
        }
      }}
    >
      <ReactHotkeys
        keyName={'Escape'}
        onKeyDown={(_, event) => {
          event.preventDefault();
          if (showReadAloud) {
            closeReadAloud();
          }
        }}
      />
      <TopCircle />
      <ClickIconContainer>
        <IconWrapper color="#61b195" height="40%" width="40%">
          <ClickSvg />
        </IconWrapper>
      </ClickIconContainer>

      {showReadAloud && lessonAttachment && (
        <ReadAloudModal onClose={closeReadAloud}>
          <ReadAloud
            lessonAttachment={lessonAttachment}
            isFromDiscover={isFromDiscover}
          />
        </ReadAloudModal>
      )}
      <span
        style={{
          position: 'absolute',
          top: '45%',
          left: 20,
          width: 'calc(100% - 40px)',
          textAlign: 'center',
          color: 'white',
          fontSize: `${element.width / 12}px`,
          userSelect: 'none',
        }}
      >
        {title}
      </span>
    </div>
  );
};

const ClickIconContainer = styled.div`
  position: absolute;
  top: 15%;
  left: 45%;
  height: 40%;
  width: 40%;
  display: grid;
  justify-content: left;
  align-items: start;
`;

const TopCircle = styled.div`
  height: 50%;
  width: 50%;

  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -40%);

  border-radius: 9999px;
  background: #93c8b6;
  box-shadow: 0px 8px 1px #61b195;
`;

export default SlideElementAttachmentPassage;
